/**
 * Fulfilment orchestration data access (P45 U74; FSD 5.2.5): the allocation engine, fulfilment
 * orders, release to the warehouse and pick confirmation.
 *
 * Thin wrappers over the generated client, like `@/modules/sales/api`. Two rules this file keeps:
 *
 *  - **A write takes the caller's Idempotency-Key** (`idempotencyKey: string`); nothing here
 *    mints one. The two PREVIEWS (`previewAllocationRun`, `previewPrioritise`) send none: the
 *    server runs them inside a savepoint it rolls back, so they write no row, audit entry, event
 *    or number, and a key would only make the idempotency store replay a stale answer.
 *  - **Only parameters that carry meaning travel**: an empty filter never reaches the query.
 *
 * Release of an allocation (`POST /sales-allocations/{id}/release`) is not an idempotent route on
 * the server, so its wrapper takes no key; a repeat is refused by the status check (an allocation
 * that is no longer `allocated` cannot be released again).
 */

import {
  type ConfirmPickRequest,
  type ListAllocationsQuery,
  type ListFulfilmentOrdersQuery,
  type ListPicksQuery,
  type PagePickDTO,
  type PageSalesFulfilmentOrderDTO,
  type PageStockAllocationDTO,
  type PickDTO,
  type ReleaseAllocationRequest,
  type SalesAllocationRunDTO,
  type SalesAllocationRunRequest,
  type SalesFulfilmentOrderDTO,
  type SalesFulfilmentPrioritiseDTO,
  type SalesFulfilmentPrioritiseRequest,
  type SalesFulfilmentReleaseDTO,
  type SalesFulfilmentReleaseRequest,
  type SalesManualAllocationRequest,
  type StockAllocationDTO,
  salesAllocationsList,
  salesAllocationsManual,
  salesAllocationsRelease,
  salesFulfilmentAllocate,
  salesFulfilmentOrdersGet,
  salesFulfilmentOrdersList,
  salesFulfilmentOrdersPrioritise,
  salesFulfilmentOrdersRelease,
  salesPicksConfirm,
  salesPicksGet,
  salesPicksList,
} from '@erpx/api'
import type { Tone } from '@erpx/ui'

import { api } from '@/api/client'

/* ------------------------------------------------------------- permissions */

/**
 * The permission codes the fulfilment screens act under (`internal/sales/permissions.go` and
 * `fulfilment_ps_codes.go`). Kept beside the wrappers rather than in `sales/permissions.ts`
 * because this packet does not own that file; the codes are the server's, verbatim.
 */
export const FULFILMENT_PERMISSIONS = {
  /** GET /sales-allocations and /sales-fulfilment-orders are read under the order read. */
  orderRead: 'sales.order.read',
  /** Run the engine, release (un-reserve) an allocation. */
  allocationManage: 'sales.allocation.manage',
  /** Manual allocation ahead of the rule, with a justification (reported). */
  allocationOverride: 'sales.allocation.override',
  /** Raise or lower a fulfilment order's priority with a reason. */
  fulfilmentPrioritise: 'sales.fulfilment.prioritise',
  /** Release a fulfilment order to the warehouse (issues its pick list). */
  fulfilmentRelease: 'sales.fulfilment.release',
  pickRead: 'sales.pick.read',
  /** Confirm a pick. */
  pickManage: 'sales.pick.manage',
  /** Draft a shipment from picks, cancel a draft. */
  shipmentCreate: 'sales.shipment.create',
  /** Confirm despatch, mark in transit, ask for early despatch. */
  despatchConfirm: 'sales.despatch.confirm',
  /** Record delivery (proof of delivery) and a failed delivery. */
  podRecord: 'sales.pod.record',
} as const

/* -------------------------------------------------------------- cache keys */

export const FULFILMENT_KEYS = {
  all: ['sales', 'fulfilment'] as const,
  orders: ['sales', 'fulfilment', 'orders'] as const,
  ordersPage: (params: Record<string, unknown>) => ['sales', 'fulfilment', 'orders', 'list', params] as const,
  order: (id: number) => ['sales', 'fulfilment', 'orders', 'one', id] as const,
  allocations: ['sales', 'fulfilment', 'allocations'] as const,
  allocationsPage: (params: Record<string, unknown>) =>
    ['sales', 'fulfilment', 'allocations', 'list', params] as const,
  picks: ['sales', 'fulfilment', 'picks'] as const,
  picksPage: (params: Record<string, unknown>) => ['sales', 'fulfilment', 'picks', 'list', params] as const,
  pick: (id: number) => ['sales', 'fulfilment', 'picks', 'one', id] as const,
} as const

/* ------------------------------------------------------------------ vocab */

export type AllocationRule = SalesAllocationRunRequest['rule']
/** The rules a RUN may use; `manual` is a line-by-line act of its own (the server refuses it here). */
export const RUN_RULES: readonly Exclude<AllocationRule, 'manual'>[] = [
  'fifo',
  'customer_priority',
  'order_value',
  'contractual',
]
export const ALL_RULES: readonly AllocationRule[] = [...RUN_RULES, 'manual']

export type AllocationStatus = StockAllocationDTO['status']
export const ALLOCATION_STATUSES: readonly AllocationStatus[] = ['allocated', 'picked', 'despatched', 'released']

export type FulfilmentStatus = SalesFulfilmentOrderDTO['status']
export const FULFILMENT_STATUSES: readonly FulfilmentStatus[] = [
  'open',
  'released',
  'picking',
  'picked',
  'despatched',
  'closed',
  'cancelled',
]

export type PickStatus = PickDTO['status']
export const PICK_STATUSES: readonly PickStatus[] = ['open', 'picking', 'picked', 'cancelled']

export type ReleaseStrategy = SalesFulfilmentReleaseRequest['strategy']
export const RELEASE_STRATEGIES: readonly ReleaseStrategy[] = ['wave', 'route', 'customer']

/* ---------------------------------------------- the server's rules, mirrored */

/** `PrioritiseFulfilmentOrder`: only an open or released fulfilment order is reprioritised. */
export function mayPrioritise(order: Pick<SalesFulfilmentOrderDTO, 'status'>): boolean {
  return order.status === 'open' || order.status === 'released'
}

/**
 * `ReleaseFulfilmentOrder`: only an OPEN fulfilment order that still holds allocated stock is
 * released (a credit-held order is refused too; that is only known to the server).
 */
export function mayReleaseToWarehouse(order: Pick<SalesFulfilmentOrderDTO, 'status' | 'allocatedQuantity'>): boolean {
  return order.status === 'open' && isPositiveQty(order.allocatedQuantity)
}

/** `ReleaseAllocation`: only an `allocated` allocation is released back to available stock. */
export function mayReleaseAllocation(allocation: Pick<StockAllocationDTO, 'status'>): boolean {
  return allocation.status === 'allocated'
}

/** `ConfirmPick`: an open or picking pick is confirmed; a picked or cancelled one is not. */
export function mayConfirmPick(pick: Pick<PickDTO, 'status'>): boolean {
  return pick.status === 'open' || pick.status === 'picking'
}

/** Priority is a whole number between 0 and 100 (the server's range check). */
export function isValidPriority(text: string): boolean {
  if (!/^\d{1,3}$/.test(text.trim())) return false
  const value = Number.parseInt(text.trim(), 10)
  return value >= 0 && value <= 100
}

/** A decimal string strictly above zero, without JS number arithmetic on its digits. */
export function isPositiveQty(value: string | null | undefined): boolean {
  if (typeof value !== 'string') return false
  const text = value.trim()
  if (!/^\d+(\.\d+)?$/.test(text)) return false
  return /[1-9]/.test(text)
}

/** Today's business date in a time zone, YYYY-MM-DD (the server's `todayFor`). */
export function todayIn(timeZone: string | undefined, now: Date = new Date()): string {
  return dateIn(timeZone, now)
}

/** The business date an instant falls on in a time zone, YYYY-MM-DD. */
export function dateIn(timeZone: string | undefined, instant: Date): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timeZone === undefined || timeZone === '' ? undefined : timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(instant)
  } catch {
    return instant.toISOString().slice(0, 10)
  }
}

/* ---------------------------------------------------------- fulfilment orders */

export interface FulfilmentOrderFilters {
  companyId?: number | null
  orderId?: number | null
  customerId?: number | null
  locationId?: number | null
  status?: FulfilmentStatus | null
  cursor?: string
  limit?: number
}

export function listFulfilmentOrders(filters: FulfilmentOrderFilters): Promise<PageSalesFulfilmentOrderDTO> {
  const query: ListFulfilmentOrdersQuery = {
    ...(filters.limit === undefined ? {} : { limit: filters.limit }),
    ...(filters.cursor === undefined || filters.cursor === '' ? {} : { cursor: filters.cursor }),
    ...(filters.companyId === undefined || filters.companyId === null ? {} : { companyId: filters.companyId }),
    ...(filters.orderId === undefined || filters.orderId === null ? {} : { orderId: filters.orderId }),
    ...(filters.customerId === undefined || filters.customerId === null ? {} : { customerId: filters.customerId }),
    ...(filters.locationId === undefined || filters.locationId === null ? {} : { locationId: filters.locationId }),
    ...(filters.status === undefined || filters.status === null ? {} : { status: filters.status }),
  }
  return salesFulfilmentOrdersList(api(), query)
}

export function getFulfilmentOrder(id: number): Promise<SalesFulfilmentOrderDTO> {
  return salesFulfilmentOrdersGet(api(), { id: String(id) })
}

/** The reallocation a priority change would cause. Read-only: the server rolls it back. */
export function previewPrioritise(
  id: number,
  body: Omit<SalesFulfilmentPrioritiseRequest, 'preview'>,
): Promise<SalesFulfilmentPrioritiseDTO> {
  return salesFulfilmentOrdersPrioritise(api(), { id: String(id) }, { ...body, preview: true })
}

export function prioritise(
  id: number,
  body: Omit<SalesFulfilmentPrioritiseRequest, 'preview'>,
  idempotencyKey: string,
): Promise<SalesFulfilmentPrioritiseDTO> {
  return salesFulfilmentOrdersPrioritise(api(), { id: String(id) }, { ...body, preview: false }, idempotencyKey)
}

export function releaseToWarehouse(
  id: number,
  body: SalesFulfilmentReleaseRequest,
  idempotencyKey: string,
): Promise<SalesFulfilmentReleaseDTO> {
  return salesFulfilmentOrdersRelease(api(), { id: String(id) }, body, idempotencyKey)
}

/* ------------------------------------------------------------- allocations */

export interface AllocationFilters {
  orderId?: number | null
  fulfilmentOrderId?: number | null
  warehouseId?: number | null
  status?: AllocationStatus | null
  rule?: AllocationRule | null
  cursor?: string
  limit?: number
}

export function listAllocations(filters: AllocationFilters): Promise<PageStockAllocationDTO> {
  const query: ListAllocationsQuery = {
    ...(filters.limit === undefined ? {} : { limit: filters.limit }),
    ...(filters.cursor === undefined || filters.cursor === '' ? {} : { cursor: filters.cursor }),
    ...(filters.orderId === undefined || filters.orderId === null ? {} : { orderId: filters.orderId }),
    ...(filters.fulfilmentOrderId === undefined || filters.fulfilmentOrderId === null
      ? {}
      : { fulfilmentOrderId: filters.fulfilmentOrderId }),
    ...(filters.warehouseId === undefined || filters.warehouseId === null ? {} : { warehouseId: filters.warehouseId }),
    ...(filters.status === undefined || filters.status === null ? {} : { status: filters.status }),
    ...(filters.rule === undefined || filters.rule === null ? {} : { rule: filters.rule }),
  }
  return salesAllocationsList(api(), query)
}

/** Every allocation of one order (bounded: an order has few lines). */
export async function listOrderAllocations(orderId: number): Promise<StockAllocationDTO[]> {
  const out: StockAllocationDTO[] = []
  let cursor = ''
  for (let pageNo = 0; pageNo < 10; pageNo += 1) {
    const page = await listAllocations({ orderId, limit: 200, cursor })
    out.push(...page.items)
    if (!page.hasMore || page.nextCursor === null || page.nextCursor === undefined || page.nextCursor === '') break
    cursor = page.nextCursor
  }
  return out
}

/** What a run would allocate. Read-only: the server rolls it back and returns ids of nothing. */
export function previewAllocationRun(body: Omit<SalesAllocationRunRequest, 'preview'>): Promise<SalesAllocationRunDTO> {
  return salesFulfilmentAllocate(api(), { ...body, preview: true })
}

export function runAllocation(
  body: Omit<SalesAllocationRunRequest, 'preview'>,
  idempotencyKey: string,
): Promise<SalesAllocationRunDTO> {
  return salesFulfilmentAllocate(api(), { ...body, preview: false }, idempotencyKey)
}

export function manualAllocation(
  body: SalesManualAllocationRequest,
  idempotencyKey: string,
): Promise<StockAllocationDTO> {
  return salesAllocationsManual(api(), body, idempotencyKey)
}

/** Un-reserve an allocation, with the reason (required) and an optional short code. */
export function releaseAllocation(id: number, body: ReleaseAllocationRequest): Promise<StockAllocationDTO> {
  return salesAllocationsRelease(api(), { id: String(id) }, body)
}

/* ------------------------------------------------------------------- picks */

export interface PickFilters {
  companyId?: number | null
  orderId?: number | null
  warehouseId?: number | null
  fulfilmentOrderId?: number | null
  status?: PickStatus | null
  waveId?: string
  cursor?: string
  limit?: number
}

export function listPicks(filters: PickFilters): Promise<PagePickDTO> {
  const query: ListPicksQuery = {
    ...(filters.limit === undefined ? {} : { limit: filters.limit }),
    ...(filters.cursor === undefined || filters.cursor === '' ? {} : { cursor: filters.cursor }),
    ...(filters.companyId === undefined || filters.companyId === null ? {} : { companyId: filters.companyId }),
    ...(filters.orderId === undefined || filters.orderId === null ? {} : { orderId: filters.orderId }),
    ...(filters.warehouseId === undefined || filters.warehouseId === null ? {} : { warehouseId: filters.warehouseId }),
    ...(filters.fulfilmentOrderId === undefined || filters.fulfilmentOrderId === null
      ? {}
      : { fulfilmentOrderId: filters.fulfilmentOrderId }),
    ...(filters.status === undefined || filters.status === null ? {} : { status: filters.status }),
    ...(filters.waveId === undefined || filters.waveId.trim() === '' ? {} : { waveId: filters.waveId.trim() }),
  }
  return salesPicksList(api(), query)
}

/** Every completed pick of one order, with its lines (bounded). */
export async function listOrderPicks(orderId: number, status?: PickStatus): Promise<PickDTO[]> {
  const out: PickDTO[] = []
  let cursor = ''
  for (let pageNo = 0; pageNo < 10; pageNo += 1) {
    const page = await listPicks({ orderId, limit: 200, cursor, ...(status === undefined ? {} : { status }) })
    out.push(...page.items)
    if (!page.hasMore || page.nextCursor === null || page.nextCursor === undefined || page.nextCursor === '') break
    cursor = page.nextCursor
  }
  return out
}

export function getPick(id: number): Promise<PickDTO> {
  return salesPicksGet(api(), { id: String(id) })
}

export function confirmPick(id: number, body: ConfirmPickRequest, idempotencyKey: string): Promise<PickDTO> {
  return salesPicksConfirm(api(), { id: String(id) }, body, idempotencyKey)
}

/* ------------------------------------------------------------------ tones */

export function fulfilmentStatusTone(status: FulfilmentStatus): Tone {
  switch (status) {
    case 'released':
    case 'picking':
      return 'info'
    case 'picked':
    case 'despatched':
    case 'closed':
      return 'success'
    case 'cancelled':
      return 'danger'
    case 'open':
    default:
      return 'neutral'
  }
}

export function allocationStatusTone(status: AllocationStatus): Tone {
  switch (status) {
    case 'allocated':
      return 'info'
    case 'picked':
    case 'despatched':
      return 'success'
    case 'released':
      return 'warning'
    default:
      return 'neutral'
  }
}

export function pickStatusTone(status: PickStatus): Tone {
  switch (status) {
    case 'picking':
      return 'info'
    case 'picked':
      return 'success'
    case 'cancelled':
      return 'danger'
    case 'open':
    default:
      return 'neutral'
  }
}
